/**
 * Helpers for turning arbitrary Redux / axios error payloads into something
 * a user can actually read. Centralized here so every container doesn't
 * re-implement (or forget) the `typeof === "object"` dance.
 *
 * Shapes the app receives:
 *   - `"Server error"`                       — plain string from thunk
 *   - `{ detail: "Bad request" }`            — DRF error detail
 *   - `{ field: ["msg1", "msg2"] }`          — DRF field validation
 *   - `{ field: "msg" }`                     — single-string field error
 *   - `{ non_field_errors: [...] }`          — DRF-level errors
 *   - nested combinations of the above
 *
 * `flattenError` never yields `[object Object]` — non-string values are
 * stringified, and nested objects are flattened with dotted field names.
 */

// DRF wrapper keys that carry a message but aren't a user-visible field —
// rendering "detail: Not found." reads like a stack trace.
const WRAPPER_FIELDS = new Set(["detail", "non_field_errors", "error", "errors", "message"]);

/**
 * Turn an API field name into label-ish text: "street_address" → "Street
 * address", "images.file" → "Images file". Wrapper keys become null so the
 * message renders alone.
 * @param {string} field
 * @returns {string | null}
 */
function humanizeField(field) {
  if (WRAPPER_FIELDS.has(field)) return null;
  const words = field.replace(/[._]+/g, " ").trim();
  if (!words) return null;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Replace raw axios transport errors with copy a user can act on. Server-
 * provided messages pass through untouched.
 * @param {string} message
 * @returns {string}
 */
function humanizeMessage(message) {
  if (/^network error$/i.test(message)) {
    return "We couldn't reach the server — check your connection and try again.";
  }
  if (/^timeout of .* exceeded$/i.test(message)) {
    return "The request timed out — please try again.";
  }
  const statusMatch = message.match(/^Request failed with status code (\d+)$/i);
  if (statusMatch) {
    const status = Number(statusMatch[1]);
    return status >= 500
      ? "Something went wrong on our end — please try again in a moment."
      : "That request didn't go through — please try again.";
  }
  return message;
}

/**
 * @param {unknown} error
 * @returns {Array<{ field: string | null; message: string }>}
 */
function flattenRaw(error) {
  if (error == null) return [];
  if (typeof error === "string") {
    return [{ field: null, message: humanizeMessage(error) }];
  }
  if (typeof error !== "object") {
    return [{ field: null, message: String(error) }];
  }
  const entries = /** @type {[string, unknown][]} */ (Object.entries(error));
  const out = [];
  for (const [field, value] of entries) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        out.push({ field, message: humanizeMessage(String(item)) });
      }
    } else if (typeof value === "object") {
      // Nested — prefix the field name so the user can still find it.
      for (const nested of flattenRaw(value)) {
        const combined = nested.field ? `${field}.${nested.field}` : field;
        out.push({ field: combined, message: nested.message });
      }
    } else {
      out.push({ field, message: humanizeMessage(String(value)) });
    }
  }
  return out;
}

/**
 * @param {unknown} error
 * @returns {Array<{ field: string | null; message: string }>}
 */
export function flattenError(error) {
  return flattenRaw(error).map(({ field, message }) => ({
    field: field == null ? null : humanizeField(field),
    message,
  }));
}
